import { useMutation, useQueryClient, type UseMutationResult } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface ChangeFixedWeekdaysInput {
  contractId: string;
  weekdays: number[];
}

const ERROR_CODES = new Set([
  "contract_not_found",
  "permission_denied",
  "contract_not_active",
  "contract_not_weekly",
  "weekday_count_mismatch",
  "invalid_weekday",
]);

/**
 * Troca os dias fixos de uma matrícula ativa a partir de hoje. A RPC solta as
 * diárias futuras do desenho antigo, grava os dias e materializa o ciclo numa
 * única transação.
 */
export async function changeFixedWeekdays({ contractId, weekdays }: ChangeFixedWeekdaysInput): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await (supabase as any).rpc("set_daycare_plan_contract_days", {
    p_contract_id: contractId,
    p_weekdays: [...weekdays].sort((a, b) => a - b),
  });
  if (error) throw error;
}

export function useChangeFixedWeekdays(): UseMutationResult<void, unknown, ChangeFixedWeekdaysInput> {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("patients");

  return useMutation({
    mutationFn: changeFixedWeekdays,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["stay-plan-contracts"] });
      queryClient.invalidateQueries({ queryKey: ["stay-plan-rows"] });
      queryClient.invalidateQueries({ queryKey: ["daycare-hotel"] });
      toast({ title: t("planCard.editDays.saved") });
    },
    onError: (error: unknown) => {
      const message = (error as { message?: string } | null)?.message ?? "";
      const code = ERROR_CODES.has(message) ? message : "unknown";
      toast({ variant: "destructive", title: t(`planCard.editDays.errors.${code}`) });
    },
  });
}
